import { useState } from 'react';
import {
  useInTransit, usePhysicalCounts, useWarehouseBins, useReorderSuggestions,
  useSlowMoving, useCreatePhysicalCount, useCountAction, useCreateWarehouseBin,
  useTransferAction, useTrace, useLocations,
  type InTransitRecord, type PhysicalCountRecord, type TraceEntry,
} from '../hooks/useInventoryApi';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { getApiErrorMessage } from '@/shared/api/axios';
import { Truck, ClipboardCheck, Warehouse, Search, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { localToday } from '@/shared/utils/utils';

// ── Local helpers (not exported: keeps this a components-only module) ─────────

function money(minor: number | null | undefined) {
  return '₹' + ((minor ?? 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 });
}

function shortDate(d: string | null | undefined) {
  return d ? new Date(d).toLocaleDateString('en-IN') : '—';
}

const BADGE: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  frozen: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
  counting: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  pending_approval: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  posted: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  in_transit: 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  partially_received: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  received: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  reorder: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  normal: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

function Badge({ value }: { value?: string }) {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE[value] ?? BADGE.normal}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

const TH = 'px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider';
const TD = 'px-3 py-2 text-sm';

function Loading() {
  return <div className="space-y-2 p-4">{[0, 1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>;
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-muted-foreground text-center py-12">{text}</p>;
}

// ── In-Transit ────────────────────────────────────────────────────────────────
// Receive/cancel go through useTransferAction (/api/inventory/transfers/*),
// which is the single public contract for transfer mutations.

export function TransitTab() {
  const { data, isLoading, error } = useInTransit();
  const action = useTransferAction();
  const records = data?.items ?? [];

  const outstanding = (r: InTransitRecord) =>
    (r.lines ?? []).reduce(
      (sum, l) => sum + (l.shippedQty - (l.receivedQty ?? 0) - (l.damagedQty ?? 0) - (l.rejectedQty ?? 0)),
      0,
    );

  const run = (r: InTransitRecord, act: 'receive' | 'cancel') => {
    if (!r.sourceId) {
      toast.error('This in-transit record is not linked to a transfer.');
      return;
    }
    action.mutate({ id: r.sourceId, action: act }, {
      onSuccess: () => toast.success(act === 'receive' ? 'Transfer received.' : 'Transfer cancelled, stock returned to source.'),
      onError: (err) => toast.error(getApiErrorMessage(err, `Could not ${act} the transfer.`)),
    });
  };

  if (isLoading) return <Card><Loading /></Card>;
  if (error) return <Card><CardBody><p className="text-sm text-destructive">{getApiErrorMessage(error, 'Could not load in-transit stock.')}</p></CardBody></Card>;

  return (
    <Card>
      <CardHeader
        title={<span className="flex items-center gap-2"><Truck className="h-4 w-4 text-violet-600" />Stock In Transit</span>}
        subtitle="Dispatched stock that has left the source but is not yet received at the destination."
      />
      <CardBody className="p-0">
        {records.length === 0 ? <Empty text="Nothing is in transit right now." /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className={TH}>Transit No</th><th className={TH}>Source Doc</th>
                  <th className={TH}>Shipped</th><th className={TH}>Expected</th>
                  <th className={TH}>Carrier</th><th className={TH}>Outstanding Qty</th>
                  <th className={TH}>Value</th><th className={TH}>Status</th><th className={TH}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {records.map(r => (
                  <tr key={r._id} className="hover:bg-muted/20">
                    <td className={`${TD} font-semibold`}>{r.transitNo}</td>
                    <td className={TD}>{r.sourceDocNo || r.sourceType}</td>
                    <td className={TD}>{shortDate(r.shippedAt)}</td>
                    <td className={TD}>{shortDate(r.expectedArrival)}</td>
                    <td className={TD}>{r.carrier || '—'}</td>
                    <td className={`${TD} font-medium`}>{outstanding(r)}</td>
                    <td className={`${TD} font-medium`}>{money(r.totalValueMinor)}</td>
                    <td className={TD}><Badge value={r.status} /></td>
                    <td className={TD}>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" disabled={action.isPending} onClick={() => run(r, 'receive')}>
                          Receive
                        </Button>
                        {r.status === 'in_transit' && (
                          <Button size="sm" variant="ghost" disabled={action.isPending} onClick={() => run(r, 'cancel')}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ── Physical Counts ───────────────────────────────────────────────────────────

/** Next lifecycle step available for a count, or null when nothing is pending. */
function nextCountStep(status: string): { action: 'freeze' | 'submit' | 'approve' | 'post'; label: string } | null {
  switch (status) {
    case 'draft': return { action: 'freeze', label: 'Freeze Snapshot' };
    case 'counting': return { action: 'submit', label: 'Submit for Approval' };
    case 'pending_approval': return { action: 'approve', label: 'Approve Variance' };
    case 'approved': return { action: 'post', label: 'Post Variance' };
    default: return null;
  }
}

export function CountsTab() {
  const { data, isLoading, error } = usePhysicalCounts();
  const action = useCountAction();
  const [isModalOpen, setModalOpen] = useState(false);
  const counts = data?.items ?? [];

  const run = (c: PhysicalCountRecord) => {
    const step = nextCountStep(c.status);
    if (!step) return;
    action.mutate({ id: c._id, action: step.action }, {
      onSuccess: () => toast.success(`${step.label} completed.`),
      onError: (err) => toast.error(getApiErrorMessage(err, `Could not ${step.label.toLowerCase()}.`)),
    });
  };

  return (
    <>
      <NewCountModal open={isModalOpen} onClose={() => setModalOpen(false)} />
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-blue-600" />Physical &amp; Cycle Counts</span>}
          subtitle="Freeze a snapshot, count, review variance, approve, then post. Posting creates stock movements — balances are never overwritten."
          action={
            <Button size="sm" onClick={() => setModalOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> New Count
            </Button>
          }
        />
        <CardBody className="p-0">
          {isLoading ? <Loading />
            : error ? <p className="text-sm text-destructive p-4">{getApiErrorMessage(error, 'Could not load counts.')}</p>
            : counts.length === 0 ? <Empty text="No counts yet. Create one to begin a stock count." />
            : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className={TH}>Count No</th><th className={TH}>Type</th><th className={TH}>Snapshot</th>
                      <th className={TH}>Items</th><th className={TH}>Variance Qty</th>
                      <th className={TH}>Variance Value</th><th className={TH}>Status</th><th className={TH}>Next Step</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {counts.map(c => {
                      const step = nextCountStep(c.status);
                      const variance = c.totalVarianceValueMinor ?? 0;
                      return (
                        <tr key={c._id} className="hover:bg-muted/20">
                          <td className={`${TD} font-semibold`}>{c.countNo}</td>
                          <td className={TD}>{c.countType}</td>
                          <td className={TD}>{shortDate(c.snapshotDate)}</td>
                          <td className={TD}>{c.snapshotData?.length ?? 0}</td>
                          <td className={TD}>{c.totalVarianceQty ?? 0}</td>
                          <td className={`${TD} font-medium ${variance < 0 ? 'text-red-600' : variance > 0 ? 'text-emerald-600' : ''}`}>
                            {money(variance)}
                          </td>
                          <td className={TD}><Badge value={c.status} /></td>
                          <td className={TD}>
                            {step ? (
                              <Button size="sm" variant="outline" disabled={action.isPending} onClick={() => run(c)}>
                                {step.label}
                              </Button>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </CardBody>
      </Card>
    </>
  );
}

function NewCountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: locations } = useLocations(open);
  const create = useCreatePhysicalCount();
  const [countType, setCountType] = useState('full');
  const [locationId, setLocationId] = useState('');
  const [snapshotDate, setSnapshotDate] = useState(localToday());
  const [notes, setNotes] = useState('');

  const reset = () => { setCountType('full'); setLocationId(''); setNotes(''); };

  const submit = () => {
    if (!locationId) { toast.error('Choose the location to count.'); return; }
    create.mutate({ countType, locationId, snapshotDate, notes }, {
      onSuccess: () => { toast.success('Count created. Freeze the snapshot to begin.'); reset(); onClose(); },
      onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the count.')),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="New Physical Count">
      <div className="space-y-4">
        <Select
          label="Count Type"
          value={countType}
          onChange={(e) => setCountType(e.target.value)}
          options={[
            { label: 'Full Physical Count', value: 'full' },
            { label: 'Cycle Count', value: 'cycle' },
            { label: 'Spot Check', value: 'spot' },
          ]}
        />
        <Select
          label="Location"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          options={[
            { label: 'Select a location…', value: '' },
            ...(locations ?? []).map(l => ({ label: `${l.name} (${l.code})`, value: l.id })),
          ]}
        />
        <Input label="Snapshot Date" type="date" value={snapshotDate} onChange={(e) => setSnapshotDate(e.target.value)} />
        <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        <p className="text-xs text-muted-foreground">
          Freezing records the system quantity for every item in scope. Variance is only written to stock after approval.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create Count'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Warehouse Bins ────────────────────────────────────────────────────────────

const BIN_TYPES = ['rack', 'shelf', 'bin', 'tank', 'silo', 'yard', 'cold_room', 'quarantine_zone', 'staging', 'dispatch', 'receiving'];
const OWNERSHIP_TYPES = ['owned', 'consignment_in', 'consignment_out', 'third_party'];

export function BinsTab() {
  const { data, isLoading, error } = useWarehouseBins();
  const [isModalOpen, setModalOpen] = useState(false);
  const bins = data?.items ?? [];

  return (
    <>
      <NewBinModal open={isModalOpen} onClose={() => setModalOpen(false)} />
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Warehouse className="h-4 w-4 text-emerald-600" />Bins, Tanks &amp; Sub-Locations</span>}
          subtitle="Storage positions inside a warehouse — racks, shelves, bins, tanks, quarantine and staging zones."
          action={
            <Button size="sm" onClick={() => setModalOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> New Bin
            </Button>
          }
        />
        <CardBody className="p-0">
          {isLoading ? <Loading />
            : error ? <p className="text-sm text-destructive p-4">{getApiErrorMessage(error, 'Could not load bins.')}</p>
            : bins.length === 0 ? <Empty text="No bins defined yet." />
            : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className={TH}>Code</th><th className={TH}>Name</th><th className={TH}>Type</th>
                      <th className={TH}>Zone</th><th className={TH}>Aisle</th>
                      <th className={TH}>Ownership</th><th className={TH}>Temp Controlled</th><th className={TH}>Active</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bins.map(b => (
                      <tr key={b._id} className="hover:bg-muted/20">
                        <td className={`${TD} font-semibold`}>{b.code}</td>
                        <td className={TD}>{b.name || '—'}</td>
                        <td className={TD}>{(b.binType ?? '').replace(/_/g, ' ')}</td>
                        <td className={TD}>{b.zone || '—'}</td>
                        <td className={TD}>{b.aisle || '—'}</td>
                        <td className={TD}>{(b.ownershipType ?? '').replace(/_/g, ' ')}</td>
                        <td className={TD}>{b.temperatureControlled ? 'Yes' : 'No'}</td>
                        <td className={TD}><Badge value={b.isActive ? 'approved' : 'draft'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </CardBody>
      </Card>
    </>
  );
}

function NewBinModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: locations } = useLocations(open);
  const create = useCreateWarehouseBin();
  const [form, setForm] = useState({
    locationId: '', code: '', name: '', binType: 'bin',
    zone: '', aisle: '', ownershipType: 'owned', temperatureControlled: false,
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.locationId) { toast.error('Choose the parent location.'); return; }
    if (!form.code.trim()) { toast.error('Bin code is required.'); return; }
    create.mutate(form, {
      onSuccess: () => {
        toast.success('Bin created.');
        setForm({ locationId: '', code: '', name: '', binType: 'bin', zone: '', aisle: '', ownershipType: 'owned', temperatureControlled: false });
        onClose();
      },
      onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the bin.')),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="New Warehouse Bin">
      <div className="space-y-4">
        <Select
          label="Parent Location"
          value={form.locationId}
          onChange={(e) => set('locationId', e.target.value)}
          options={[
            { label: 'Select a location…', value: '' },
            ...(locations ?? []).map(l => ({ label: `${l.name} (${l.code})`, value: l.id })),
          ]}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Bin Code" value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="A-01-02" />
          <Input label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Optional" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Bin Type"
            value={form.binType}
            onChange={(e) => set('binType', e.target.value)}
            options={BIN_TYPES.map(t => ({ label: t.replace(/_/g, ' '), value: t }))}
          />
          <Select
            label="Ownership"
            value={form.ownershipType}
            onChange={(e) => set('ownershipType', e.target.value)}
            options={OWNERSHIP_TYPES.map(t => ({ label: t.replace(/_/g, ' '), value: t }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Zone" value={form.zone} onChange={(e) => set('zone', e.target.value)} placeholder="Optional" />
          <Input label="Aisle" value={form.aisle} onChange={(e) => set('aisle', e.target.value)} placeholder="Optional" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.temperatureControlled}
            onChange={(e) => set('temperatureControlled', e.target.checked)}
            className="rounded border-input"
          />
          Temperature controlled
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create Bin'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Reorder Planning ──────────────────────────────────────────────────────────
// Deliberately shows only reorder *suggestions*. The plain low-stock list stays
// on the existing "Low Stock Alerts" tab so the two do not duplicate each other.

export function ReorderTab() {
  const { data, isLoading, error } = useReorderSuggestions();
  const suggestions = data?.suggestions ?? [];

  return (
    <Card>
      <CardHeader
        title="Reorder Planning"
        subtitle="Items at or below their reorder point, with a suggested order quantity based on max/min levels and recent consumption. Suggestions are advisory — no purchase order is raised automatically."
      />
      <CardBody className="p-0">
        {isLoading ? <Loading />
          : error ? <p className="text-sm text-destructive p-4">{getApiErrorMessage(error, 'Could not load reorder suggestions.')}</p>
          : suggestions.length === 0 ? <Empty text="No items are below their reorder point." />
          : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className={TH}>Item</th><th className={TH}>SKU</th><th className={TH}>Available</th>
                    <th className={TH}>Reorder Pt</th><th className={TH}>Safety</th>
                    <th className={TH}>Suggested Qty</th><th className={TH}>Days Cover</th>
                    <th className={TH}>Lead Time</th><th className={TH}>Urgency</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {suggestions.map(s => (
                    <tr key={s.productId} className="hover:bg-muted/20">
                      <td className={`${TD} font-medium`}>{s.name}</td>
                      <td className={TD}>{s.sku || '—'}</td>
                      <td className={TD}>{s.currentAvailable}</td>
                      <td className={TD}>{s.reorderPoint}</td>
                      <td className={TD}>{s.safetyStock}</td>
                      <td className={`${TD} font-semibold text-primary`}>{s.suggestedOrderQty}</td>
                      <td className={TD}>{s.daysOfStockRemaining ?? '—'}</td>
                      <td className={TD}>{s.leadTimeDays} d</td>
                      <td className={TD}><Badge value={s.urgency} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </CardBody>
    </Card>
  );
}

// ── Aging / Slow Moving ───────────────────────────────────────────────────────

export function AgingTab() {
  const [period, setPeriod] = useState(90);
  const { data, isLoading, error } = useSlowMoving(period);
  const nonMoving = data?.nonMoving ?? [];
  const slowMoving = data?.slowMoving ?? [];
  const deadValue = data?.totalDeadStockValueMinor ?? 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Dead Stock (No Movement)"
          subtitle="Items holding stock with no outbound movement in the selected period."
          action={
            <div className="flex items-center gap-3">
              {deadValue > 0 && (
                <span className="text-sm font-bold text-rose-600 whitespace-nowrap">{money(deadValue)} tied up</span>
              )}
              <Select
                value={String(period)}
                onChange={(e) => setPeriod(Number(e.target.value))}
                options={[30, 60, 90, 180, 365].map(d => ({ label: `Last ${d} days`, value: String(d) }))}
              />
            </div>
          }
        />
        <CardBody className="p-0">
          {isLoading ? <Loading />
            : error ? <p className="text-sm text-destructive p-4">{getApiErrorMessage(error, 'Could not load the ageing analysis.')}</p>
            : nonMoving.length === 0 ? <Empty text="Every stocked item moved within the selected period." />
            : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className={TH}>Item</th><th className={TH}>SKU</th><th className={TH}>On Hand</th>
                      <th className={TH}>Stock Value</th><th className={TH}>Last Movement</th><th className={TH}>Days Idle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {nonMoving.map(i => (
                      <tr key={`${i.productId}-${i.locationId ?? 'all'}`} className="hover:bg-muted/20">
                        <td className={`${TD} font-medium`}>{i.name}</td>
                        <td className={TD}>{i.sku || '—'}</td>
                        <td className={TD}>{i.quantityOnHand}</td>
                        <td className={`${TD} font-medium`}>{money(i.stockValueMinor)}</td>
                        <td className={TD}>{shortDate(i.lastMovementAt)}</td>
                        <td className={TD}>{i.daysSinceLastMovement ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </CardBody>
      </Card>

      {slowMoving.length > 0 && (
        <Card>
          <CardHeader title="Slow Moving" />
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className={TH}>Item</th><th className={TH}>SKU</th><th className={TH}>On Hand</th>
                    <th className={TH}>Moved in Period</th><th className={TH}>Stock Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {slowMoving.map(i => (
                    <tr key={`${i.productId}-${i.locationId ?? 'all'}`} className="hover:bg-muted/20">
                      <td className={`${TD} font-medium`}>{i.name}</td>
                      <td className={TD}>{i.sku || '—'}</td>
                      <td className={TD}>{i.quantityOnHand}</td>
                      <td className={TD}>{i.totalMovedInPeriod ?? 0}</td>
                      <td className={`${TD} font-medium`}>{money(i.stockValueMinor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ── Traceability ──────────────────────────────────────────────────────────────

function TraceTable({ title, rows }: { title: string; rows: TraceEntry[] }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">{title}</h4>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full">
          <thead className="border-b bg-muted/30">
            <tr>
              <th className={TH}>Date</th><th className={TH}>Movement</th><th className={TH}>Reference</th>
              <th className={TH}>In</th><th className={TH}>Out</th><th className={TH}>Narration</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map(m => (
              <tr key={m.movementId} className="hover:bg-muted/20">
                <td className={TD}>{shortDate(m.date)}</td>
                <td className={TD}>{(m.movementType ?? '').replace(/_/g, ' ')}</td>
                <td className={TD}>{m.referenceNumber || m.referenceType || '—'}</td>
                <td className={TD}>{m.quantityIn ?? '—'}</td>
                <td className={TD}>{m.quantityOut ?? m.quantity ?? '—'}</td>
                <td className={`${TD} text-xs text-muted-foreground`}>{m.narration || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TraceabilityTab() {
  const trace = useTrace();
  const [productId, setProductId] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');

  const run = (kind: 'batch' | 'serial' | 'recall') => {
    if (!productId.trim()) { toast.error('Product ID is required to run a trace.'); return; }
    if (kind === 'serial' && !serialNumber.trim()) { toast.error('Serial number is required for a serial trace.'); return; }
    if (kind !== 'serial' && !batchNumber.trim()) { toast.error('Batch number is required for a batch trace or recall.'); return; }

    trace.mutate({ kind, productId, batchNumber, serialNumber }, {
      onError: (err) => toast.error(getApiErrorMessage(err, 'The trace could not be completed.')),
    });
  };

  const result = trace.data;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Search className="h-4 w-4 text-primary" />Batch &amp; Serial Traceability</span>}
          subtitle="Trace a batch upstream to its supplier receipt and downstream to customer deliveries, or run a recall report."
        />
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Input label="Product ID" value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Catalog product id" />
            <Input label="Batch Number" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder="For batch trace / recall" />
            <Input label="Serial Number" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="For serial trace" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={trace.isPending} onClick={() => run('batch')}>Trace Batch</Button>
            <Button size="sm" variant="outline" disabled={trace.isPending} onClick={() => run('serial')}>Trace Serial</Button>
            <Button size="sm" variant="outline" disabled={trace.isPending} onClick={() => run('recall')}>Recall Report</Button>
          </div>
        </CardBody>
      </Card>

      {trace.isPending && <Card><Loading /></Card>}

      {result && !trace.isPending && (
        <Card>
          <CardHeader
            title="Trace Result"
            subtitle={
              <span className="flex flex-wrap gap-4">
                {result.summary && (
                  <>
                    <span>Received: <strong>{result.summary.totalReceived}</strong></span>
                    <span>Issued: <strong>{result.summary.totalIssued}</strong></span>
                    <span>Movements: <strong>{result.summary.movementCount}</strong></span>
                  </>
                )}
                {result.currentStatus && <span>Status: <Badge value={result.currentStatus} /></span>}
                {result.totalQtyIssued !== undefined && (
                  <span>Qty in recall scope: <strong>{result.totalQtyIssued}</strong></span>
                )}
              </span>
            }
          />
          <CardBody className="space-y-5">
            <TraceTable title="Upstream — Supplier Source" rows={result.upstream ?? []} />
            <TraceTable title="Downstream — Customer Destination" rows={result.downstream ?? []} />
            <TraceTable title="Serial Lifecycle" rows={result.lifecycle ?? []} />
            <TraceTable title="Affected Documents (Recall)" rows={result.affectedDocuments ?? []} />
            {!result.upstream?.length && !result.downstream?.length && !result.lifecycle?.length && !result.affectedDocuments?.length && (
              <Empty text="No movements found for that batch or serial." />
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
